/* eslint-disable lit-a11y/accessible-name */
import { PolymerElement, html } from 'polymer';

import type { Moment } from 'moment';

import { BehaviorFlyout } from '@eab-eip/hi-behavior-flyout/hi-behavior-flyout';
import { BehaviorInputTime } from '@eab-eip/hi-behavior-input/hi-behavior-input-time';
import { BehaviorMirror } from '@eab-eip/hi-behavior-mirror/hi-behavior-mirror';
import {
  a11yLabel,
  AsHIC,
  BehaviorA11yGestures,
  BehaviorA11yKeyboard,
  BehaviorElement,
  HIP
} from '@eab-eip/hi-component/hi-component';

import dom from '@eab-pip/pi-dom';

import { TimeListElement } from './hi-time-list';
import './hi-time-list';

const moment = globalThis['moment'];

/**
 * @customElement
 * @polymer
 * @appliesMixin BehaviorFlyout
 * @appliesMixin BehaviorA11yKeyboard
 * @appliesMixin BehaviorA11yGestures
 * @appliesMixin BehaviorInteractiveElement
 * @appliesMixin BehaviorElement
 * @appliesMixin BehaviorInputTime
 */
export class TimepickerElement extends BehaviorInputTime(
  BehaviorFlyout(BehaviorMirror(BehaviorA11yKeyboard(BehaviorA11yGestures(BehaviorElement(AsHIC(PolymerElement))))))
) {
  static get is() {
    return 'hi-timepicker';
  }

  static get template() {
    return html`
      <style is="custom-style" include="hi-theme"></style>
      <style>
        :host {
          --hi-timepicker--input-border: var(--hi-px-1) solid var(--hi-clr--gray-6);
          --hi-timepicker--input-width: calc(calc(var(--hi-px-32) * 2) + var(--hi-px-32));
          --hi-timepicker--input-background-color: var(--hi-clr--white);
          --hi-timepicker--input-padding: var(--hi-px-8);
          --hi-timepicker--flyout-min-width: auto;
        }

        :host {
          width: var(--hi-timepicker--input-width);
          display: inline-block;
          position: relative;
        }

        :host([disabled]) #input {
          --hi-timepicker--input-background-color: var(--hi-clr--gray-3);
        }

        :host([disabled]) #timepicker-icon {
          background: var(--hi-clr--gray-3);
        }

        #timepicker-icon {
          position: absolute;
          right: var(--hi-px-8);
          top: 50%;
          transform: translateY(-50%);
          pointer-events: none;
          background: var(--hi-clr--white);
        }

        #input {
          background-color: var(--hi-timepicker--input-background-color);
          border: var(--hi-timepicker--input-border);
          border-radius: var(--hi-shape--radius);
          padding: var(--hi-timepicker--input-padding);
          width: 100%;
          font-size: var(--hi-font-size--text);
        }

        #input:focus {
          outline: var(--hi-outline--width) var(--hi-outline--line) var(--hi-clr--outline);
          outline-offset: calc(var(--hi-outline--offset) * -3);
        }

        :host([validated][invalid]):not([silent-validate]) #input,
        :host([dirty][invalid]:not([focused]):not([silent-validate])) #input {
          box-shadow: var(--hi-clr--error) 0 0 var(--hi-px-2);
          border-color: var(--hi-clr--error);
        }

        #flyoutContainer {
          display: none;
          position: absolute;
          list-style-type: none;
          padding: 0;
          margin: 0;
          max-height: 35vh;
          overflow-x: auto;
          background-color: var(--hi-clr--white);
          border: var(--hi-px-1) solid var(--hi-clr--gray-4);
          z-index: 1;
          border-radius: var(--hi-shape--radius);
          min-width: var(--hi-timepicker--flyout-min-width);
        }

        :host([opened]) #flyoutContainer {
          display: block;
        }
      </style>
      <div id="flyoutValue" aria-expanded="false" role="combobox" aria-controls="flyoutContainer">
        <input
          id="input"
          mixin="input-placeholder"
          type="text"
          on-focus="__onFocus_input"
          autocomplete="off"
          inputmode="numeric"
          autofocus$="[[autofocus]]"
          disabled$="[[disabled]]"
          name$="[[name]]"
          placeholder$="[[placeholder]]"
          required$="[[required]]"
          value="{{value::input}}"
        />
        <hi-icon id="timepicker-icon" kind="clock" theme$="[[ theme ]]"></hi-icon>
        <hi-time-list
          id="flyoutContainer"
          min="[[min]]"
          max="[[max]]"
          step="[[step]]"
          display-format="[[displayFormat]]"
          on-item-focus="__onItemFocus__timeList"
          on-time-select="__onTimeSelect__timeList"
        ></hi-time-list>
      </div>
    `;
  }

  $: {
    input: HTMLInputElement;
    flyoutContainer: TimeListElement;
  };

  // value: string | HTMLElement;
  placeholder: string;
  autocomplete: string;
  // autofocus: boolean;
  name: string;
  scrollStartTime: string;
  forceRoundTime: boolean;
  stepInternal: string;
  timeFormat: string;

  selected = 0;
  readonly times: string[];

  timeParseFormats: string[];

  displayFormat = 'h:mm A';
  max = '11:59pm';
  min = '12:00am';
  step = 15;

  static get properties() {
    return {
      value: {
        type: Object,
        reflectToAttribute: true,
        notify: true
      },
      placeholder: {
        type: String
      },
      autocomplete: {
        type: String
      },
      autofocus: {
        type: String
      },
      ariaDescribedby: {
        type: String
      },
      ariaLabel: {
        type: String
      },
      ariaLabelledby: {
        type: String
      },
      name: {
        type: String
      },
      scrollStartTime: {
        type: String
      },
      forceRoundTime: {
        type: Boolean,
        value: false
      },
      stepInternal: {
        type: String,
        computed: '__computeStepInternal(step)'
      },
      timeFormat: {
        type: String,
        readOnly: true,
        value: () => TimepickerElement.timeFormat
      },
      max: {
        type: String
      },
      min: {
        type: String
      },
      step: {
        type: Number
      },
      displayFormat: {
        type: String
      },
      timeParseFormats: {
        type: Array,
        readOnly: true,
        value: () => ['h:mm', 'hmm', 'h', 'h:mma', 'hmma', 'ha', 'HHmm']
      }
    };
  }

  static get observers() {
    return ['_ariaLabelTextWatcher(ariaLabel, ariaLabelledby, ariaDescribedby)'];
  }

  static get keyBindings() {
    return {
      'esc:keyup': '_flyout__onKeyUp_escape',
      'down:keydown': '__onKeyDown_down',
      'enter:keyup': '__onKeyUp_enter',
      'space:keyup': '__onKeyUp_space',
      'up:keydown': '__onKeyDown_up'
    };
  }

  static get valueEvent() {
    return 'value-changed';
  }

  static get autoBinding() {
    return {
      signal: 'value-changed',
      field: 'value'
    };
  }

  get inputMirrorType() {
    return 'text';
  }

  connectedCallback() {
    super.connectedCallback();
    this._kindFlyout = 'sticky';
    this.__onBlur_timepicker();

    dom.setAttrs(this.$.input, {
      'aria-autocomplete': 'list',
      focusable: true
    });
    if (this.disabled) {
      this.$['timepicker-icon'].setAttribute('theme', 'gray');
    }

    if (this.value) {
      this.flush();
    }
  }

  static get timeFormat() {
    return 'h:mm A';
  }

  static get api() {
    return ['roundTime', 'parseTime', 'parseUserTime'];
  }

  roundTime(_time: string, step = 5) {
    const time = moment(_time);

    return time
      .clone()
      .startOf('hour')
      .add(Math.round(time.minute() / step) * step, 'minutes');
  }

  parseUserTime(time: string) {
    return moment(time, this.timeParseFormats, true);
  }

  /**
   * @note currently duplicated in hi-behavior-input-time to share with datepicker
   * @todo we should move to [hi-component]
   *
   */
  parseTime(time: string, format = this.timeFormat || 'h:mm A') {
    if (typeof time === 'object') {
      return time;
    }

    return time === 'now' ? moment() : moment(time, format);
  }

  // TODO: The flyout container should "flip" orientation if outside the boundary of the viewport
  toggle() {
    this.$.input.removeAttribute('aria-activedescendant');
    this._flyout__toggle();
    this._flyout__adjustToContainer();
  }

  protected _flyout__adjustToContainer() {
    this.$.flyoutContainer.style.width = `${this.offsetWidth}px`;
  }

  __onFocus_input() {
    if (!this.opened) {
      this.toggle();

      if (this.scrollStartTime || this.$.input.value) {
        this.scrollToTime((this.scrollStartTime || this.$.input.value) as string);
      }
    }
  }

  __onBlur_timepicker() {
    this.addEventListener('blur', (event: unknown) => {
      const getRelatedTargetEl = event.relatedTarget;
      if (!getRelatedTargetEl) {
        this.flush();
        return;
      }

      const parentEl = this.parentElement;
      if (getRelatedTargetEl.contains(parentEl)) {
        return;
      } else {
        this.flush();
      }
    });
  }

  flush() {
    const time = this.$.input.value as string;
    if (!time) {
      this.opened = false;
      this.validate();
      return;
    }

    const hasMinTime = this.min != null;
    const minTime = this.parseTime(this.min);

    const hasMaxTime = this.max != null;
    const maxTime = this.parseTime(this.max);

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    let mTime = this.parseUserTime(time.replace(/\s+/g, '')) as any;

    if (mTime.isValid()) {
      this.invalid = false;

      if (this.forceRoundTime) {
        mTime = this.roundTime(mTime, this.step);
      }

      if (hasMinTime && mTime.isBefore(minTime)) {
        mTime = minTime.format(this.displayFormat);
      } else if (hasMaxTime && !mTime.isBetween(minTime, maxTime, null, '[]')) {
        mTime = maxTime.format(this.displayFormat);
      } else {
        mTime = mTime.format(this.displayFormat) as Moment;
      }

      this.$.flyoutContainer._dispatchBubblingEvent('time-select', { time: mTime });
    } else {
      mTime = mTime.format(this.displayFormat);
      this.$.flyoutContainer._dispatchBubblingEvent('time-select', { time: mTime });
    }

    this.value = mTime;
    this.validate();

    if (this.opened) {
      this.toggle();
    }
  }

  __onItemFocus__timeList(event: CustomEvent) {
    const container = this.$.flyoutContainer;
    const focusedElement = event.detail.item;
    this.$.flyoutContainer.$.timesList.focus();

    this.$.input.setAttribute('aria-activedescendant', focusedElement.getAttribute('id'));

    if (container.scrollHeight > container.offsetHeight) {
      const scrollPosition = container.offsetHeight + container.scrollTop;
      const elementPosition = focusedElement.offsetTop + focusedElement.offsetHeight;

      if (elementPosition > scrollPosition) {
        container.scrollTop = elementPosition - container.offsetHeight;
      } else if (focusedElement.offsetTop < container.scrollTop) {
        container.scrollTop = focusedElement.offsetTop;
      }
    }
  }

  __onTimeSelect__timeList(event: CustomEvent) {
    this.$.input.value = event.detail.time;
    this.value = this.$.input.value;
    this.toggle();
  }

  scrollToTime(time: string) {
    const timeElement = this.$.flyoutContainer.findTime(time);

    if (timeElement) {
      this.$.flyoutContainer.scrollTop = timeElement.offsetTop;
    }
  }

  __onKeyDown_up() {
    if (this.opened) {
      this.$.flyoutContainer.shiftFocusUp();
    } else {
      this.toggle();
    }
  }

  __onKeyDown_down() {
    if (this.opened) {
      this.$.flyoutContainer.shiftFocusDown();
    } else {
      this.toggle();
    }
  }

  __onKeyUp_enter() {
    if (this.opened) {
      this.$.flyoutContainer.chooseFocusedTime();
    }
  }

  __onKeyUp_space() {
    if (this.opened) {
      this.$.flyoutContainer.chooseFocusedTime();
    }
  }

  __computeStepInternal(step: number) {
    return step ? step * 60 : undefined;
  }

  _ariaLabelTextWatcher(label: string, labelledBy: string, describedBy: string) {
    a11yLabel.writeA11yLabel(this.$.input, label, labelledBy, describedBy, { ns: this.ns });
  }
}

HIP.factory.element(TimepickerElement);
